import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from "rxjs";
import { AuthActions } from "./auth-action.types";
import { AuthTokenKey, MarketingKey } from "@shared/local-storage.key";
import { PlatformService } from "@core/services/platform.service";
import { LogoFaviconHttpService } from "@core/services/logo-favicon-http.service";
import { SupportChatService } from "@core/services/support-chat.service";

@Injectable({
  providedIn: 'root'
})
export class AuthEffects {

  constructor(
    private platformService: PlatformService,
    private logoFaviconHttpService: LogoFaviconHttpService,
    private supportChatService: SupportChatService,
  ) { }

  login$ = createEffect(() =>
    inject(Actions).pipe(
      ofType(AuthActions.Login),
      tap(async action => {
        if (this.platformService.isBrowser()) {
          localStorage.setItem(AuthTokenKey, JSON.stringify(action));

          // `legacySessionKeys` (meta-reducer) has already written `user_data` by the time
          // effects run, so the profile-derived session keys are read back from there.
          this.setRewardNotice();

          // Re-arm the insufficient-funds popup for the new session; app.component only
          // opens it while this key is absent (see onRefreshBalance).
          localStorage.removeItem('insufficient_balance_popup');

          // Refetch (not just clear) the logo on login. The logo refetch otherwise rides on
          // popUpAnnouncementEmitter, which does not fire on a normal login, so a bare
          // clearLogoFavicon() left the header logo blank until a reload.
          // updateLogoAndFavicon() pulls the current-locale logo and sets it in place.
          this.logoFaviconHttpService.updateLogoAndFavicon();

          /* AIO-82885: attach the member to the chat widget. A no-op until the widget is
             installed — SupportChatService.start() identifies again once it is, which is what
             covers a reload, because this effect fires at rehydration before that request
             comes back. */
          this.supportChatService.signedIn();
        }
      })
    ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    inject(Actions).pipe(
      ofType(AuthActions.Logout),
      tap(() => {
        if (this.platformService.isBrowser()) {
          localStorage.removeItem(AuthTokenKey);
          localStorage.removeItem(MarketingKey);
          localStorage.removeItem('reward_notice');
          localStorage.removeItem('insufficient_balance_popup');

          this.logoFaviconHttpService.clearLogoFavicon();

          /* AIO-82885: the widget must forget this member. reset(), never clear() — see
             SkynetService.forget. template-02 has had Crisp.session.reset() here for years;
             this repo has never had any chat reset on logout at all. */
          this.supportChatService.signedOut();
        }
      })
    ),
    { dispatch: false });

  /**
   * `reward_notice` gates the insufficient-funds popup (it only opens while the value is
   * exactly '0'). Before this, a normal form login never wrote the key at all, so the read
   * returned null and the popup could never fire -- only the cross-login path set it.
   */

  private setRewardNotice() {
    try {
      const userData = localStorage.getItem('user_data');
      const pendingReward = userData ? +JSON.parse(userData)?.pending_reward : 0;
      localStorage.setItem('reward_notice', pendingReward > 0 ? '1' : '0');
    } catch {
      localStorage.setItem('reward_notice', '0');
    }
  }
}
